import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { getGitHubAuthUrl } from "../api";
import { useAuth } from "../auth/AuthContext";
import PrCard from "../components/PrCard";
import GitHubIcon from "../components/GitHubIcon";
import { heroPrs } from "../components/marketing/heroFixtures";

// Reuses the landing page's own fixture (already shaped to match the real
// GET /api/prs payload, and its "dmitri-k" persona isn't a real person tied
// to this project) rather than inventing a second, duplicate fixture file.
const FEATURED_PR = heroPrs[0];

/**
 * The CodeRabbit-inspired intermediate signup screen (item 20) — every
 * "Start free trial" CTA across the marketing site lands here now, instead
 * of going straight to GitHub's install URL. Two jobs: restate the trial
 * terms right at the commitment moment, and catch a wrong guess ("Already
 * have an account?") before any GitHub round-trip at all.
 *
 * The page itself is NOT auth-aware — it always renders the same way
 * regardless of session state, no redirect-on-load. Reversed 2026-08-22:
 * originally the plan was "an already-logged-in visitor just does one
 * harmless extra GitHub round-trip if they click through" — live testing
 * proved that wrong. If you already have the GitHub App installed, the
 * install URL doesn't offer a fresh install; it drops you on GitHub's own
 * configure screen, which doesn't round-trip back to us at all — the exact
 * dead end already fixed once before for the old "Sign in" button (commit
 * a78d33d), just reachable through a new door. Fixed at the button's click
 * handler instead of the page level, per the project owner's explicit call:
 * an already-authenticated click on "Continue with GitHub" goes straight to
 * /dashboard, never touching GitHub.
 *
 * Same dead end resurfaced 2026-08-24 for a *logged-out* returning user
 * (registered, GitHub App already installed, but no session in this
 * browser) — indistinguishable from a first-time visitor client-side, so it
 * always fell through to the install URL and hit the same GitHub
 * reverification/configure-screen trap. Fixed by sending the logged-out
 * branch through getGitHubAuthUrl() instead — the exact URL Login.js already
 * uses — so it round-trips through auth-callback regardless of install
 * state: an existing user lands straight on /dashboard via their existing
 * team_id, and auth-callback's own installation-discovery fallback (see
 * CLAUDE.md's Auth Flow Summary) recovers anyone who installed but never
 * finished onboarding. Trade-off, confirmed with the project owner: a
 * genuinely brand-new visitor now does a plain GitHub login before landing
 * on Onboarding to install the App, instead of jumping straight into
 * GitHub's install/repo-picker in one click — matching how Login.js and
 * JoinPage already behave for a first-time sign-in.
 *
 * Bare card chrome (no MarketingNav/Footer), matching Login.js/Onboarding.js's
 * existing focused-screen convention — no navigation distraction right
 * before a conversion decision.
 */
export default function SignupPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleSignup = () => {
    if (isAuthenticated) {
      navigate("/dashboard");
      return;
    }
    window.location.href = getGitHubAuthUrl();
  };

  return (
    <div className="min-h-screen bg-rf-bg-primary flex flex-col lg:flex-row">
      {/* Left: marketing panel. Hidden below lg: — collapses entirely rather
          than squeezing two columns into a narrow viewport, matching this
          app's established "single column below the breakpoint" convention
          (Dashboard.js, AnalyticsPage.js). `overflow-hidden` contains the
          two glow layers below so neither bleeds past the panel into the
          bordered signup column on the right. */}
      <div className="hidden lg:flex relative isolate flex-[1.15] items-center justify-center overflow-hidden p-12 xl:p-16">
        {/* Panel-wide ambient wash — two corner glows (teal near the
            headline, blue trailing toward the card) so the whole left side
            reads as gradient, not just the card. `isolate` on the panel
            above is load-bearing, not decorative: `relative` alone doesn't
            establish a stacking context, so this `-z-10` layer was
            rendering behind the page's own root background and was 100%
            invisible regardless of opacity — confirmed via computed-style
            inspection, not just eyeballing a screenshot. */}
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(55% 55% at 15% 10%, rgba(0,212,170,0.10) 0%, transparent 65%), radial-gradient(50% 50% at 85% 90%, rgba(0,153,255,0.08) 0%, transparent 65%)",
          }}
          aria-hidden="true"
        />
        <div className="max-w-xl">
          <h1 className="text-3xl xl:text-4xl font-bold text-rf-text-primary tracking-tight leading-[1.15] mb-4">
            One install away from knowing which PRs actually matter.
          </h1>
          <p className="text-rf-text-secondary text-base leading-relaxed mb-8 max-w-[42ch]">
            Score complexity, flag what's critical, and route every PR to
            whoever's actually touched that code — without ever reading it.
          </p>
          <div className="flex gap-2.5 mb-10 flex-wrap">
            <span className="font-mono text-xs font-semibold px-3 py-1.5 rounded-md border border-rf-border text-rf-text-secondary bg-rf-bg-elevated">
              14-day free trial
            </span>
            <span className="font-mono text-xs font-semibold px-3 py-1.5 rounded-md border border-rf-border text-rf-text-secondary bg-rf-bg-elevated">
              No credit card required
            </span>
          </div>

          {/* Real complexity nutrition label, not a facsimile — same
              artifact ComplexitySpotlight.js reuses on the landing page.
              No more `mx-auto` — that centered the 460px card within the
              wider max-w-xl column, misaligning its left edge from the
              headline/subhead above it. Removed so it lines up flush-left
              with the text, like it should have from the start. */}
          <div className="relative isolate max-w-[460px]">
            {/* Glow behind the card — same accent-wash recipe HeroMockup.js
                already uses for the landing page's own PrCard-style mockup,
                for visual consistency between the two. `isolate` here is
                load-bearing for the same reason as the panel-wide wash
                above: without it, this -z-10 layer rendered behind the
                page's root background and was completely invisible
                regardless of opacity — confirmed via computed-style
                inspection (correct background/opacity/dimensions, wrong
                paint order). `-inset-8` instead of `inset-0` gives the blur
                room to diffuse beyond the card's own tight bounding box,
                matching the breathing room the original mockup's 56px-
                padded frame gave it. */}
            <div
              className="absolute -inset-8 -z-10 rounded-3xl blur-3xl opacity-40"
              style={{
                background:
                  "radial-gradient(60% 60% at 50% 40%, rgba(0,212,170,0.18) 0%, rgba(0,153,255,0.08) 45%, transparent 75%)",
              }}
              aria-hidden="true"
            />
            <PrCard pr={FEATURED_PR} />
          </div>
        </div>
      </div>

      {/* Right: signup card. Full-width below lg:, fixed max-width alongside
          the marketing panel above it. */}
      <div className="flex-1 lg:max-w-[480px] flex items-center justify-center p-6 sm:p-10 lg:border-l lg:border-rf-border">
        <div className="w-full max-w-sm">
          <h2 className="text-xl font-bold text-rf-text-primary mb-2.5">
            Create your account
          </h2>
          <p className="text-sm text-rf-text-secondary leading-relaxed mb-7">
            Get{" "}
            <span className="text-rf-text-primary font-semibold">
              a free 14-day trial
            </span>{" "}
            for your entire team when you connect your GitHub organization.
          </p>

          <button
            onClick={handleSignup}
            className="w-full py-3 px-6 rounded-lg bg-white text-gray-900 font-semibold text-sm hover:bg-gray-100 transition-all duration-200 flex items-center justify-center gap-3"
          >
            <GitHubIcon />
            Continue with GitHub
          </button>

          {/* Item 24: the legal line item 20 deliberately left out rather
              than ship dead links. This screen renders no MarketingFooter
              (bare card chrome), so the footer's Legal column doesn't cover
              it — it needs its own. */}
          <p className="text-rf-text-muted text-[11px] text-center leading-relaxed mt-3.5">
            By continuing you agree to our{" "}
            <Link to="/terms" className="underline hover:text-rf-text-secondary">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline hover:text-rf-text-secondary">
              Privacy Policy
            </Link>
            .
          </p>

          {/* Trust reassurance, moved up from Onboarding.js so it's seen
              before the GitHub redirect, not only after. */}
          {/*
            Was "Read-only access to your repos" until the review write-back
            shipped — the App now holds Pull requests: Write so it can request
            the reviewer it picked, which made a blanket read-only claim false
            at the exact moment someone is deciding whether to trust us.
            "Never reads your code" is the stronger claim anyway, and unlike
            read-only it stays true. See /security for the full permission list.
          */}
          <p className="text-rf-text-muted text-xs text-center leading-relaxed mt-2.5 mb-7">
            Never reads your code. Never writes to it.
            <br />
            Uninstall anytime.
          </p>

          {/* Cross-link — symmetric with /login's own "New to Anthurium?
              Start free trial" link. Catches a wrong guess in one click,
              before any GitHub round-trip. */}
          <p className="text-rf-text-secondary text-sm text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-rf-accent font-semibold hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
